import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { SEARCH_RESULT_TYPE } from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { formatCoordinates } from "common/utils/coordinates";
import { isSearchResultType } from "common/utils/search";
import { getInteractiveLayerIdsForSource } from "common-client/utils/customMapLayer";
import { isCustomMapGeometryResult } from "common-client/utils/search";
import { OmniboxSearchResult } from "../../../generated/graphql";
import { useIsScreenSize } from "../../../hooks/useWindowSize";
import { SearchContext } from "../../Search/SearchContext";
import {
  SEARCH_RESULTS_LAYER_ID,
  SearchResultsLayer,
} from "../layers/searchResults";
import { GeometryRef } from "./customMapLayer";
import {
  EventFeature,
  Feature,
  findRelevantFeature,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
} from "./types";
import { getPointFromFeature } from "./utils";

export type SearchResultsLayerConfig = LayerConfig;
const SEARCH_RESULTS_SOURCE_ID = "search-results-source";
const BASE_INTERACTIVE_LAYER_IDS: LayerId[] = [
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PROPERTY}`,
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}`,
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}-fill`,
];

const isCustomMapGeometrySourceLayer = (sourceLayer?: string) =>
  sourceLayer?.startsWith(SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY);

const getFeatureType = (sourceLayer: string): SEARCH_RESULT_TYPE => {
  if (isCustomMapGeometrySourceLayer(sourceLayer)) {
    return SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY;
  }
  if (isSearchResultType(sourceLayer)) {
    return sourceLayer;
  }
  throw new Error(`Unknown source layer: ${sourceLayer}`);
};

const getFeatureSortKey = (feature: EventFeature) =>
  feature.sourceLayer === SEARCH_RESULT_TYPE.PROPERTY ? 0
  : feature.sourceLayer === SEARCH_RESULT_TYPE.PARCEL ? 1
  : 2;

const getFeatureDisplay = (feature: EventFeature | Feature): string => {
  if (isCustomMapGeometrySourceLayer(feature.sourceLayer)) {
    return feature.properties?.label ?? "";
  }
  if (feature.properties?.address) {
    return feature.properties.address;
  }
  if (
    feature.sourceLayer === SEARCH_RESULT_TYPE.PARCEL &&
    feature.properties?.parcelNumber
  ) {
    return feature.properties.parcelNumber;
  }
  const point = getPointFromFeature(feature);
  return point ? formatCoordinates(point) : "";
};

const findCustomMapIdForGeometry = (
  featureId: string,
  results: Maybe<Array<OmniboxSearchResult>>,
): Maybe<string> => {
  const result = results?.find(r => r.id === featureId);
  return result?.__typename === SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY ?
      result.customMapId
    : null;
};

const getSourceLayerInfo = (
  featureType: SEARCH_RESULT_TYPE,
  featureId: string,
  results: Maybe<Array<OmniboxSearchResult>>,
): { sourceLayer: string; layerIdsToSearch: Array<LayerId> } => {
  if (featureType === SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY) {
    const customMapId = findCustomMapIdForGeometry(featureId, results);
    if (!customMapId) {
      return { sourceLayer: featureType, layerIdsToSearch: [] };
    }
    const sourceLayer = `${SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY}-${customMapId}`;
    return {
      sourceLayer,
      layerIdsToSearch: getInteractiveLayerIdsForSource({
        sourceId: `${SEARCH_RESULTS_LAYER_ID}-${sourceLayer}`,
      }),
    };
  }

  if (featureType === SEARCH_RESULT_TYPE.PROPERTY) {
    return {
      sourceLayer: featureType,
      layerIdsToSearch: [
        `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PROPERTY}`,
      ],
    };
  }

  return {
    sourceLayer: featureType,
    layerIdsToSearch: [
      `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}-fill`,
    ],
  };
};

const useSearchResultsHook: LayerHookBuilder<SearchResultsLayerConfig> = ({
  config,
  helpers,
}) => {
  const hoveredSearchResult = useRef<Maybe<GeometryRef>>(null);
  const { searchResultsState, searchResultsDispatch, handleSearchResultClick } =
    useContext(SearchContext);
  const isDesktop = useIsScreenSize("desktop");

  const customMapIds = useMemo(() => {
    const mapIds = new Set<string>();
    for (const result of searchResultsState.results ?? []) {
      if (isCustomMapGeometryResult(result)) {
        mapIds.add(result.customMapId);
      }
    }
    return Array.from(mapIds);
  }, [searchResultsState.results]);

  const interactiveLayerIds = useMemo<LayerId[]>(
    () => [
      ...BASE_INTERACTIVE_LAYER_IDS,
      ...customMapIds.flatMap(customMapId =>
        getInteractiveLayerIdsForSource({
          sourceId: `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.CUSTOM_MAP_GEOMETRY}-${customMapId}`,
        }),
      ),
    ],
    [customMapIds],
  );

  const clearHover = useCallback(() => {
    if (hoveredSearchResult.current) {
      helpers.emit({
        type: "removeFeatureState",
        data: { field: "hover", ...hoveredSearchResult.current },
      });
      hoveredSearchResult.current = null;
    }
  }, [helpers]);

  const updateHoverStates = useCallback(
    (featureId: string, sourceLayer: string) => {
      helpers.setFeatureState({
        id: featureId,
        field: "hover",
        source: SEARCH_RESULTS_SOURCE_ID,
        sourceLayer,
      });
      hoveredSearchResult.current = {
        id: featureId,
        source: SEARCH_RESULTS_SOURCE_ID,
        sourceLayer,
      };
    },
    [helpers],
  );

  useEffect(() => {
    clearHover();

    if (!searchResultsState.hoveredSearchResult) {
      helpers.emit({ type: "removeTooltip", data: {} });
      return;
    }

    const {
      feature: { id: hoveredFeatureId, featureType },
      hoverSource,
    } = searchResultsState.hoveredSearchResult;

    const { sourceLayer, layerIdsToSearch } = getSourceLayerInfo(
      featureType,
      hoveredFeatureId,
      searchResultsState.results,
    );

    if (!arrayHasAtLeastOneItem(layerIdsToSearch)) return;

    updateHoverStates(hoveredFeatureId, sourceLayer);

    if (hoverSource !== "results") return;

    const hoveredFeature = helpers
      .featuresByFilter({
        layer: layerIdsToSearch,
        filter: ["to-boolean", true],
      })
      .find(feature => feature.id === hoveredFeatureId);

    if (!hoveredFeature) return;

    const pixelCoordinates = helpers.getCoordinatesForFeature({
      feature: hoveredFeature,
    });
    if (pixelCoordinates) {
      helpers.emit({
        type: "setTooltip",
        data: {
          text: getFeatureDisplay(hoveredFeature),
          x: pixelCoordinates.x,
          y: pixelCoordinates.y,
        },
      });
    }
  }, [
    searchResultsState.hoveredSearchResult,
    searchResultsState.results,
    helpers,
    clearHover,
    updateHoverStates,
  ]);

  return useMemo<LayerHook>(() => {
    return {
      // -3 so that the markers show above the click object menu
      zIndex: -3,
      interactiveLayerIds,
      render: () => {
        if (!searchResultsState.results?.length || !isDesktop) {
          return null;
        }
        return <SearchResultsLayer key={SEARCH_RESULTS_LAYER_ID} />;
      },
      canHandleClick: () => !!config.interactive?.click,
      canHandleHover: () => !!config.interactive?.hover,
      onClick: ({ event }) => {
        const feature = findRelevantFeature({
          event,
          layerId: interactiveLayerIds,
          getSortKey: getFeatureSortKey,
        });
        if (!feature) return false;

        helpers.emit({ type: "removeTooltip", data: {} });
        hoveredSearchResult.current = null;

        const searchResult = searchResultsState.results?.find(
          result =>
            result.id === feature.id &&
            result.__typename === getFeatureType(feature.sourceLayer),
        );
        if (searchResult) {
          handleSearchResultClick?.(searchResult);
        }
        return true;
      },
      onHover: ({ event }) => {
        clearHover();
        const feature = findRelevantFeature({
          event,
          layerId: interactiveLayerIds,
          getSortKey: getFeatureSortKey,
        });
        if (!feature) {
          searchResultsDispatch({ type: "clearHoveredSearchResult" });
          return false;
        }

        updateHoverStates(feature.id, feature.sourceLayer);
        helpers.setCursor("pointer");
        helpers.emit({
          type: "setTooltip",
          data: {
            x: event.point.x,
            y: event.point.y,
            text: getFeatureDisplay(feature),
          },
        });
        searchResultsDispatch({
          type: "setHoveredSearchResult",
          feature: {
            id: feature.id,
            featureType: getFeatureType(feature.sourceLayer),
          },
          hoverSource: "map",
        });
        return true;
      },
      onFocusLost: () => {
        clearHover();
        searchResultsDispatch({ type: "clearHoveredSearchResult" });
      },
    };
  }, [
    config,
    helpers,
    interactiveLayerIds,
    searchResultsDispatch,
    clearHover,
    updateHoverStates,
    handleSearchResultClick,
    searchResultsState.results,
    isDesktop,
  ]);
};

export default useSearchResultsHook;
